import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from "mobx-react";
import { PrimaryButton, CustomAutocomplete, BackButtonArrow, DarkScreenMask, ConfirmModal, MyPlayLoader } from '@myplay/ui';

import { ORANGE, WHITE } from '../../styles/colors';
import { MyTeamsTable, AddTeamModal, SingleTeam, TeamsSwiper, HeaderTiles, SceneLoaderWrapper } from '../../components';
import { toTitleCase, capitalize } from '../../utils/stringManipulations';
import { NAME, TEAMS_ROUTE } from '../../utils/constants';
import I18N from '../../I18N';
import './Teams.scss';

const mockTableHeaders = ["Team", "Sport", "Season", "Coaches", "Members", "Videos", "Highlights", "Code", ""];

const Teams = ({ TeamsStore, MetricsStore, history, match }) => {
  const { teams, setSelectedTeam, selectedTeam, getTeamById, removeTeamFromClub } = TeamsStore;
  const [shouldRenderAddTeamModal, toggleAddTeamModal] = useState(false);
  const [shouldRenderSingleTeam, toggleSingleTeam] = useState(false);
  const [selectedTeamIndex, setSelectedTeamIndex] = useState(0);
  const [filteredTeams, setFilteredTeams] = useState([]);
  const [teamToDelete, setTeamToDelete] = useState(null);

  useEffect(() => {
    setFilteredTeams([...teams]);

    const params = new URLSearchParams(window.location.search);
    const teamId = params.get("teamId");
    if (teamId) {
      const team = getTeamById(teamId);
      if (team) {
        history.replace(TEAMS_ROUTE)
        handleSelectTeam(team);
      }
    }

  }, [teams.length]);

  useEffect(() => {
    if (match.isExact && shouldRenderSingleTeam) {
      handleBack();
    }
  }, [match.isExact])

  useEffect(() => {
    return () => {
      MetricsStore.setClubMetrics();
      toggleSingleTeam(false);
      setSelectedTeam(null);
    }
  }, [])

  const handleSelectTeam = team => {
    const { _id } = team;
    const selectedIndex = teams.findIndex(team => team._id === _id);
    setSelectedTeamIndex(selectedIndex);
    setSelectedTeam(_id);
    MetricsStore.setTeamMetrics(team);
    toggleSingleTeam(true);
    history.push(`${TEAMS_ROUTE}/${team._id}`);
  }

  const onSearchChange = (value, filteredItems) => {
    if (!value) {
      setFilteredTeams([...teams]);
      return;
    };

    setFilteredTeams([...filteredItems]);
  }

  const handleBack = () => {
    MetricsStore.setClubMetrics();
    toggleSingleTeam(false);
    setSelectedTeam(null);
    history.push(TEAMS_ROUTE);
  };

  const handleDeleteTeam = team => {
    setTeamToDelete(team);
  };

  const handleDeleteTeamApprove = async () => {
    await removeTeamFromClub(teamToDelete._id);
    setTeamToDelete(null);
  };

  return (
    <SceneLoaderWrapper>
      <div className="teams" data-testid="teams">
        <div className="teams__header">
          <div className="teams__header__title">
            {!shouldRenderSingleTeam ?
              <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
                <span className="teams__header__title__text">{I18N.t("MY_TEAMS").toUpperCase()}</span>
                <PrimaryButton
                  background={ORANGE}
                  onClick={() => toggleAddTeamModal(true)}
                  data-testid="add-team-button"
                >
                  <span style={{ color: WHITE }}>{toTitleCase(I18N.t("ADD_TEAM"))}</span>
                </PrimaryButton>
                <div className="teams__search-filter-container">
                  <CustomAutocomplete
                    placeholder={toTitleCase(I18N.t("SEARCH_TEAM"))}
                    items={teams}
                    propertyToFilterBy={NAME}
                    onSelect={handleSelectTeam}
                    onChange={onSearchChange}
                  />
                </div>
              </div> :
              <Fragment>
                <TeamsSwiper
                  teams={teams}
                  selectedTeamIndex={selectedTeamIndex}
                  setSelectedTeam={handleSelectTeam}
                  onEditTeamClick={() => toggleAddTeamModal(true)}
                />
                <div style={{ position: "absolute", left: 0, top: "110px" }}>
                  <BackButtonArrow onClick={handleBack} />
                </div>
              </Fragment>
            }
            <HeaderTiles />
          </div>
        </div>
        {!shouldRenderSingleTeam ?
          <MyTeamsTable
            headers={mockTableHeaders}
            tableItems={filteredTeams}
            onSelectTeam={handleSelectTeam}
            onDeleteTeam={handleDeleteTeam}
          />
          :
          <SingleTeam team={selectedTeam} />
        }
        <AddTeamModal
          isVisible={shouldRenderAddTeamModal}
          closeModal={() => toggleAddTeamModal(false)}
          isEditTeam={!!selectedTeam}
        />
        <DarkScreenMask isVisible={!!teamToDelete}>
          <ConfirmModal
            title={!!teamToDelete ? `${capitalize(I18N.t("DELETE_TEAM_CONFIRMATION"))} ${teamToDelete.name}?` : ""}
            onApprove={handleDeleteTeamApprove}
            onDismiss={() => setTeamToDelete(null)}
          />
        </DarkScreenMask>
      </div>
    </SceneLoaderWrapper>
  );
};

Teams.propTypes = {
  TeamsStore: PropTypes.object
};

Teams.defaultProps = {
  TeamsStore: {}
};

export default inject("TeamsStore", "MetricsStore", "MetaStore")(observer(Teams));